// src/pages/customer/Blog.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getBlogs } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { format } from "date-fns";
import { FiArrowRight, FiCalendar, FiUser, FiSearch } from "react-icons/fi";
import leafBg from "../../assests/background.png";

export default function Blog() {
  const [blogs,    setBlogs]   = useState([]);
  const [loading,  setLoading] = useState(true);
  const [search,   setSearch]  = useState("");

  useEffect(() => {
    getBlogs(true).then((b) => { setBlogs(b); setLoading(false); });
  }, []);

  const filtered = blogs.filter((b) =>
    !search ||
    b.title?.toLowerCase().includes(search.toLowerCase()) ||
    b.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">

      <Helmet>
        <title>Blog | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-leaf-600 via-leaf-700 to-primary-800 py-14 sm:py-16 md:py-20 px-4">
        {/* Decorative circles */}
        <span className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <span className="pointer-events-none absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/5" />

        <div className="relative max-w-3xl mx-auto text-center text-white">
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-white/60 mb-4">
            Field Notes
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-4" style={{ fontFamily: "var(--custom-body-font-family)" }}>
            Stories from the Farm
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto mb-10">
            Practical tips, seasonal insights, and news straight from the field.
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-white text-gray-800 placeholder-gray-400
                         text-sm shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
        </div>
      </section>

      {/* ── Body ─────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">

        {loading ? (
          <Spinner center />

        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-6xl mb-5">📝</p>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {search ? "No results found" : "No posts yet"}
            </h3>
            <p className="text-gray-400 text-sm">
              {search ? `Try a different search term.` : "Check back soon for farming tips and news."}
            </p>
          </div>

        ) : (
          <div className="relative overflow-hidden">
            {/* Decorative leaf branch background — sits behind the blog cards, right side */}
            <img
              src={leafBg}
              alt=""
              aria-hidden="true"
              className="absolute bottom-0 -right-16 w-[420px] sm:w-[560px] pointer-events-none select-none"
              style={{ opacity: 0.12, zIndex: 0, transform: "scaleX(-1)" }}
            />
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" style={{ zIndex: 1 }}>
            {filtered.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.id}`}
                  className="group flex flex-col rounded-2xl overflow-hidden bg-white shadow-sm
                             hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  {/* Thumbnail */}
                  <div className="aspect-[16/9] overflow-hidden bg-primary-50">
                    {blog.image ? (
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-400"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-5xl">📰</div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="flex flex-col flex-1 p-5">
                    {blog.category && (
                      <span className="text-[11px] font-semibold text-primary-600 uppercase tracking-widest mb-1.5">
                        {blog.category}
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-900 text-base leading-snug line-clamp-2 mb-2
                                   group-hover:text-primary-600 transition-colors flex-1">
                      {blog.title}
                    </h3>
                    <p className="text-sm text-gray-400 line-clamp-2 mb-4 leading-relaxed">
                      {blog.excerpt || blog.content?.replace(/<[^>]+>/g, "").slice(0, 110)}
                    </p>

                    {/* Footer row */}
                    <div className="flex items-center justify-between text-[11px] text-gray-400 pt-3
                                    border-t border-gray-100 mt-auto">
                      <div className="flex items-center gap-3">
                        {blog.author && (
                          <span className="flex items-center gap-1"><FiUser className="w-3 h-3" /> {blog.author}</span>
                        )}
                        {blog.createdAt?.toDate && (
                          <span className="flex items-center gap-1">
                            <FiCalendar className="w-3 h-3" />
                            {format(blog.createdAt.toDate(), "dd MMM yyyy")}
                          </span>
                        )}
                      </div>
                      <span className="flex items-center gap-1 text-primary-600 font-semibold
                                       group-hover:gap-2 transition-all">
                        Read <FiArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
            ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}